import Checkbox from "@/Components/Checkbox/Checkbox";
import Textfield from "@/Components/Input/Textfield";
import Select from "@/Components/Select/Select";
import useDidUpdateEffect from "@/shared/hooks/useDidUpdateEffect";
import useTimeSlotsHook from "@/shared/hooks/useTimeSlotsHook";
import { DeliveryMethods } from "@/shared/utils/enum";
import { formatDate } from "@/shared/utils/helpers";
import dayjs from "dayjs";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import usePosOrderHandOverDate, {
  defaultPosOrderHandOverDate,
} from "@/shared/atoms/usePosOrderHandOverDateAtom";
import usePosSettingsHook from "@/shared/hooks/usePosSettingsHook";

const HandoverTime = ({ selectedDelivery }) => {
  const { t } = useTranslation();
  const [date, setDate] = usePosOrderHandOverDate();
  const {
    settings: { accept_all_times_for_pos },
  } = usePosSettingsHook();

  const {
    query,
    query: { data: timeSlots },
    dateSlotsOptions,
    timeSlotsOptions,
  } = useTimeSlotsHook(selectedDelivery, date.day, accept_all_times_for_pos);

  useDidUpdateEffect(() => {
    setDate(defaultPosOrderHandOverDate);
  }, [selectedDelivery]);

  useEffect(() => {
    if (
      selectedDelivery !== DeliveryMethods.POS_ORDER &&
      !date.time &&
      !date.day
    ) {
      if (accept_all_times_for_pos || timeSlots?.length === 0) {
        setDate({
          day: formatDate(new Date(), "inputDate"),
          time: formatDate(new Date(), "inputDateTime"),
          isAsap: true,
        });
      } else if (timeSlots?.length > 0) {
        setDate({
          day: timeSlots[0]?.date,
          time: timeSlots[0]?.time_slots?.[0]?.start,
          isAsap: timeSlots[0]?.is_asap_active,
        });
      }
    }
  }, [timeSlots, date, selectedDelivery, accept_all_times_for_pos]);

  useEffect(() => {
    if (
      selectedDelivery === DeliveryMethods.POS_ORDER &&
      !date.time &&
      !date.day
    ) {
      setDate({
        day: formatDate(new Date(), "inputDate"),
        time: formatDate(new Date(), "inputDateTime"),
        isAsap: true,
      });
    }
  }, [selectedDelivery, date, accept_all_times_for_pos]);

  return (
    <div className="mb-4 flex gap-2">
      {selectedDelivery === DeliveryMethods.POS_ORDER ||
      accept_all_times_for_pos ||
      timeSlots?.length === 0 ? (
        <>
          <div className="flex-[1_0_45%]">
            <Textfield
              value={date.day}
              onChange={(e) =>
                setDate({
                  day: e.target.value,
                  time: formatDate(
                    dayjs(
                      `${e.target.value} ${
                        date.isAsap
                          ? formatDate(new Date(), "time")
                          : date.time.split(" ")[1]
                      }`,
                    ),
                    "inputDateTime",
                  ),
                  isAsap: dayjs(e.target.value).isToday(),
                })
              }
              type="date"
            />
          </div>
          <div className="flex basis-[45%] items-center">
            <div className="flex-[1_0_50%] pl-[1rem]">
              <Checkbox
                onChange={(e) =>
                  setDate({
                    day: e.target.checked
                      ? formatDate(new Date(), "inputDate")
                      : date.day,
                    time: formatDate(new Date(), "inputDateTime"),
                    isAsap: e.target.checked,
                  })
                }
                active={date.isAsap}
                checked={date.isAsap}
              >
                <div className="ml-[-1rem]">{t("asap")}</div>
              </Checkbox>
            </div>
            <div className="flex-[1_0_50%]">
              <Textfield
                disabled={date.isAsap}
                value={
                  date.isAsap
                    ? formatDate(new Date(), "time")
                    : formatDate(date.time, "time")
                }
                onChange={(e) =>
                  setDate({
                    day: date.day,
                    time: formatDate(
                      dayjs(`${date.day} ${e.target.value}`),
                      "inputDateTime",
                    ),
                    isAsap: false,
                  })
                }
                defaultValue={formatDate(new Date(), "time")}
                resize={false}
                type="time"
                name="handoverTime"
              />
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="flex-[1_0_45%]">
            <Select
              menuPlacement="top"
              value={dateSlotsOptions?.find(
                (timeSlot) => timeSlot.value === date.day
              )}
              menuPortalTarget={document.getElementById("root")}
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 1000000 }),
              }}
              onChange={(e) => {
                const selectedTimeSlots = timeSlots?.find(
                  (timeSlot) => timeSlot?.date === e.value
                );
                setDate({
                  day: e.value,
                  time: selectedTimeSlots?.time_slots?.[0]?.start,
                  isAsap: selectedTimeSlots?.is_asap_active,
                });
              }}
              name="date"
              isPending={query.isFetching}
              options={dateSlotsOptions}
            ></Select>
          </div>
          <div className="flex-[1_0_45%]">
            <Select
              menuPlacement="top"
              onChange={(e) => {
                const isAsap = e.value === "-1";
                setDate({
                  day: date.day,
                  time: isAsap ? date.time : e.value,
                  isAsap: isAsap,
                });
              }}
              value={
                date.isAsap
                  ? timeSlotsOptions[0]
                  : timeSlotsOptions?.find(
                      (timeSlot) => timeSlot.value === date.time
                    ) || timeSlotsOptions
              }
              menuPortalTarget={document.getElementById("root")}
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 1000000 }),
              }}
              isPending={query.isFetching}
              name="time"
              options={timeSlotsOptions}
            ></Select>
          </div>
        </>
      )}
    </div>
  );
};

export default HandoverTime;
